import { defineStore } from "pinia";

import AuthService from "../services/auth.service";
import { maybeShowBatteryOptimizationPrompt } from "../capacitor/battery-optimization";
import { newCache } from "../utils/cache";
import { normalizeUrlOriginPath } from "../utils/urlHelpers";
import { useIPAliasStore } from "./ipAliases";

const THREE_MINS = 3 * 60 * 1000;
const authCache = newCache(THREE_MINS);

// Define types for the store state. These mirror common/api/grant_status.go's
// GrantStatus and DelegatedIP. Keep field names in PascalCase so they match the
// JSON wire format produced by the backend.
export interface DelegatedIP {
  IP: string;
  Status: number;
  // "delegated_config" (manual), "delegated_link" (magic link), or
  // "agentic_access" (created by approving an agentic access). Agentic-sourced
  // IPs are surfaced by the agentic panel instead of the delegated panel.
  Source?: string;
  // Display name behind the device (the agentic access name); tooltip only.
  Name?: string;
  // When this device's grant lapses (RFC3339); null/absent when the grant has
  // no explicit expiry. Agentic devices carry the agentic-access expiry.
  ExpiresAt?: string | null;
}

export interface ACL {
  ACLID?: string;
  URL: string;
  Description?: string;
  Category?: string;
  Icon?: string;
  Status: number;
  ClickToGrant?: boolean;
  RequireTicketNumber?: boolean;
  RequireTOTP?: boolean;
  CGNATOpen?: boolean;
  HideFromUsers?: boolean;
  DisableForUser?: boolean;
  AutoBrowseUsers?: boolean;
  InstantReferrerRedirect?: boolean;
  GrantTypeFixedIps?: boolean;
  ExpiredAfter?: string;
  ParentID?: string;
  Error?: string;
  Config?: {
    UriToken?: string;
  };
  PropagationDelayMs?: number;
  // null is the portal default, 0 is never wait. Both must survive, so not a
  // plain optional number.
  MaxRedirectWaitMs?: number | null;
  PersistentAndAgenticEnabled?: boolean;
  PersistentIPs?: DelegatedIP[];
}

export interface User {
  ACLs?: ACL[];
  AllowAgenticAccess?: boolean;
  AllowPersistentIPs?: boolean;
  // SamlGroups are assertion group names shown when the user matches no access paths.
  SamlGroups?: string[];
  // PersistentAndAgenticEnabled is the global feature flag: when false the
  // user-facing delegated and agentic access panels are hidden.
  PersistentAndAgenticEnabled?: boolean;
  // HasSessionSlot is false when the login landed on a full session-slot pool;
  // the router keeps the user on the session-limit screen until they free a
  // slot and continue.
  HasSessionSlot?: boolean;
  [key: string]: any;
}

export interface FetchOptions {
  force?: boolean;
  ttl?: number;
}

const storeDefinition = {
  state: () => ({
    loggedIn: false,
    user: null as User | null,
    userFetchedAt: null as number | null,
    cancelledRedirects: [] as string[],
  }),

  persist: {
    pick: ["user", "userFetchedAt", "cancelledRedirects"],
  },

  actions: {
    async login(userInput: any) {
      try {
        const user = await AuthService.login(userInput);
        this.loggedIn = true;
        // Fire-and-forget: on native Android, surface the in-app overlay
        // explaining the battery-optimisation exemption so the background
        // IP-polling service is less likely to be killed by vendor power
        // management. No-op on web or once the exemption is already granted.
        maybeShowBatteryOptimizationPrompt().catch(() => {});
        return user as User;
      } catch (err) {
        this.loggedIn = false;
        this.user = null;
        this.userFetchedAt = null;
        throw err;
      }
    },

    async fetchStatus(options: FetchOptions = {}) {
      try {
        const user = await authCache({
          state: this,
          key: "user",
          fetchFn: () => AuthService.status(),
          ...options,
        });

        this.loggedIn = true;

        // Load IP aliases when user session is established
        useIPAliasStore().fetchAliases();

        return user as User;
      } catch (err) {
        this.resetAndClearPersisted();
        throw err;
      }
    },

    markRedirectCancelled(url: string | null | undefined) {
      if (!url || typeof url !== "string") return;
      const key = normalizeUrlOriginPath(url);
      const filtered = this.cancelledRedirects.filter((entry) => entry !== key);
      filtered.unshift(key);
      this.cancelledRedirects = filtered.slice(0, 20);
    },

    isRedirectCancelled(url: string | null | undefined): boolean {
      if (!url || typeof url !== "string") return false;
      return this.cancelledRedirects.includes(normalizeUrlOriginPath(url));
    },

    async logout() {
      try {
        await AuthService.logout();
      } finally {
        this.resetAndClearPersisted();
      }
    },

    hasFreshUser() {
      if (!this.user || !this.userFetchedAt) {
        return false;
      }
      return Date.now() - this.userFetchedAt < THREE_MINS;
    },

    // Mark the cached user as holding no session slot. Called when a request
    // 409s with sessionSlotRequired: the server has decided this session lost
    // its slot, so the cached HasSessionSlot must agree or the route guard,
    // trusting a stale "true", bounces the user straight off SessionLimit.
    markSessionSlotUnavailable() {
      if (this.user) {
        this.user.HasSessionSlot = false;
      }
    },

    // Overridden at boot by resetAndClearPersistedPlugin in main-user.ts.
    resetAndClearPersisted() {
      this.$reset();
    },
  },
};

// Create the store with proper typing
export const useAuthStore = defineStore("auth", storeDefinition);

export type AuthStore = ReturnType<typeof useAuthStore>;
